// WhatItShowsPicker — the ONE question of the files library, "What does it
// show?", as a CATALOG a person walks in two taps (FRONTEND-PATTERNS §Files):
// a search on top (three letters — the fast path); the KINDS present at the
// site as chips wearing their glyph (the options read — never the site's unit
// list). A kind's chip IS the answer: tapped, it says "the generator, in
// general" and opens its things — the units of that kind on the selection
// family's rows (the unit lookup's `at_place` rule inside the site, 20 at a time,
// "More — keep typing" past them — ADR-078 §6 · ADR-080), the machine's
// suggestion pinned first under its certainty; naming one makes the answer
// specific (the chip reads "Battery · Narada 12NDT200 · BAT-07", the kind is
// implied by the unit, never stored twice); tapped again, the chip clears its
// kind. Then the TOPICS no unit carries (safety · civil · access · damage ·
// general …) as plain chips, eight then "+n more". Nothing shows as implied,
// nothing is locked. The search flattens the catalog: the matching units across
// kinds through the same lookup, the matching topics as chips. Escape cancels
// here and goes no further (the peek's own Escape stays out); Enter in the
// search picks the one thing it left. The units met on any list are
// remembered, so a pick keeps its name and its kind once its list is gone; the
// ones the file already names are named by id.
//
// Two homes, one surface, and the picker carries NO commit row in either: in the
// file peek the answer is a VERB on a saved file, so the HOST commits it — the
// panel's own footer becomes Cancel · Save while the picker is open (`ContextPanel
// commit`), never scrolled out of reach under a long list; in the upload form the
// answer is a FIELD and the form's footer is the one commit. `onCancel` is the
// picker's Escape (leave it out where Escape is the form's). `onChange` hands the NAMES of the picked
// units beside the value, so a host may say them once the picker is gone.

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { matchesQuery } from '@ems-app/schemas/common/search-fold';
import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import type { AssetLookupRow } from '@ems-app/schemas/assets/lookup';
import { Button } from '../ui/Button.js';
import { ChoiceTiles, type ChoiceTileOption } from '../ui/ChoiceTiles.js';
import { PickerList } from '../ui/PickerList.js';
import { SearchField } from '../ui/SearchField.js';
import { icons } from '../ui/icons.js';
import { useLookup, type LookupSource } from '../../hooks/useLookup.js';
import { optionRowOf, type OptionRow } from '../../hooks/useOptions.js';
import { formatNumber } from '../../lib/format.js';
import { CATEGORY_ICON } from './assetsDisplay.js';
import { useAssetFace } from './lookupFaces.js';

export interface PickerTopic {
  value: string;
  label: string;
}
/** The answer: the units picked and the topics picked BY HAND (a unit's own kind is implied, never stored twice). */
export interface WhatItShowsValue {
  assets: string[];
  topics: string[];
}
/** The names of the picked units as the picker knows them ("product · reference"), keyed by id. */
export type PickedNames = ReadonlyMap<string, string>;

export interface WhatItShowsPickerProps {
  projectId: string;
  /** The site whose units are offered — the file's site (an asset's or a ticket's: its site). */
  siteId: string;
  /** The kinds present at the site (the options read), in the charter's order — the chips. */
  kinds: readonly AssetCategory[];
  /** The whole topic vocabulary with its words — the kinds' and the tail's (the host's `all_topics`). */
  topics: PickerTopic[];
  value: WhatItShowsValue;
  onChange: (value: WhatItShowsValue, names: PickedNames) => void;
  /** The unit never offered — an asset-born file's own anchor (a fact of its home, not an answer). */
  exclude?: string;
  /** The open suggestion — its kind's list shows at rest, the suggested unit pinned first under its certainty. */
  suggestion?: { asset_id: string; label: string; confidence: number | null } | null;
  /** The walk's accelerator: the previous photo's answer, one tap. */
  previous?: { label: string; value: WhatItShowsValue } | null;
  /** Focus the search on mount (the walk's C key). */
  focusSearch?: boolean;
  /** Escape cancels (the peek — its host commits through the panel's footer). Leave it out inside a form. */
  onCancel?: () => void;
  /** The phone: finger-sized rows. */
  stacked?: boolean;
  className?: string;
}

/** Topic chips shown at rest before "+n more". */
const TOPIC_CAP = 8;
/** The "+n more" / "Fewer" chip's value in the topics row. */
const MORE = '__more__';
/** The pinned suggestion's group (its heading says the certainty). */
const SUGGESTED = 'suggested';

/** The kinds the picked units imply, once each, in the picked order — from what the picker knows of each unit. */
export function impliedTopics(picked: readonly string[], categoryOf: (id: string) => string | undefined): string[] {
  const out: string[] = [];
  for (const id of picked) {
    const c = categoryOf(id);
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

export function WhatItShowsPicker({ projectId, siteId, kinds, topics, value, onChange, exclude, suggestion = null, previous = null, focusSearch = false, onCancel, stacked = false, className = '' }: WhatItShowsPickerProps) {
  const { t } = useTranslation('files');
  const face = useAssetFace();
  const [query, setQuery] = useState('');
  const [moreTopics, setMoreTopics] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // preventScroll: the caller positions the section; the cursor just lands.
    if (focusSearch) searchRef.current?.focus({ preventScroll: true });
  }, [focusSearch]);

  // ── the search across kinds, and the names of the units the answer already holds (and the suggestion's) — ONE
  //    source, the site's units; every kind's list is a window over it and remembers its rows THERE (the hook's
  //    `memory`), so a pick keeps its name and its kind once its list is gone ──
  const typed = query.trim().length > 0;
  const siteUnits = useMemo<LookupSource<'assets'>>(() => ({ entity: 'assets', params: { rule: 'at_place', site: siteId, self: exclude } }), [siteId, exclude]);
  const toName = useMemo(() => [...new Set([...value.assets, ...(suggestion ? [suggestion.asset_id] : [])])], [value.assets, suggestion]);
  // No kind present (no register, or no asset read — the host hands none): no unit is asked, by search or by id.
  const search = useLookup(projectId, siteUnits, toName, { enabled: kinds.length > 0, open: typed, text: query });
  const rowOf = useCallback((id: string): AssetLookupRow | undefined => search.known.get(id), [search.known]);
  const categoryOf = useCallback((id: string): string | undefined => rowOf(id)?.category, [rowOf]);
  const nameOf = useCallback(
    (id: string): string => {
      const row = rowOf(id);
      if (!row) return '…';
      const f = face(row);
      return f.selectedLabel ?? f.label;
    },
    [rowOf, face],
  );
  // The names handed over: the units the picker KNOWS — an unnamed id is left out (the host names it by id, never "…").
  const namesOf = useCallback((ids: readonly string[]): PickedNames => new Map(ids.flatMap((id) => (rowOf(id) ? [[id, nameOf(id)] as const] : []))), [rowOf, nameOf]);
  const emit = (next: WhatItShowsValue) => onChange(next, namesOf(next.assets));

  const labelOf = (code: string) => topics.find((tp) => tp.value === code)?.label ?? code;
  const iconOf = (code: string) => CATEGORY_ICON[code as AssetCategory];
  const implied = useMemo(() => impliedTopics(value.assets, categoryOf), [value.assets, categoryOf]);
  // The tail: the topics that are not a kind present here (a kind absent from the site reads as a plain topic).
  const tail = useMemo(() => topics.filter((tp) => !(kinds as readonly string[]).includes(tp.value)), [topics, kinds]);
  const suggestionCategory = suggestion ? (categoryOf(suggestion.asset_id) ?? null) : null;

  const setAssets = (next: string[]) => emit({ assets: next, topics: value.topics.filter((tp) => !impliedTopics(next, categoryOf).includes(tp)) });
  const setTopics = (next: string[]) => emit({ assets: value.assets, topics: next.filter((tp) => !implied.includes(tp)) });

  // ── the kind chips — the answer: each carrying its unit when one is named, else its name alone ──
  const namedIn = (kind: string) => value.assets.filter((id) => categoryOf(id) === kind);
  const answered = (kind: string) => value.topics.includes(kind) || namedIn(kind).length > 0;
  const chipLabel = (kind: string): string => {
    const named = namedIn(kind);
    if (named.length === 1) return `${labelOf(kind)} · ${nameOf(named[0]!)}`;
    if (named.length > 1) return `${labelOf(kind)} · ${t('picker.pickedCount', { count: named.length })}`;
    return labelOf(kind);
  };
  const kindTiles: ChoiceTileOption[] = kinds.map((kind) => ({ value: kind, icon: iconOf(kind), label: chipLabel(kind) }));
  const toggleKind = (next: string[]) => {
    const on = kinds.filter(answered);
    const added = next.find((kind) => !on.includes(kind as AssetCategory));
    const removed = on.find((kind) => !next.includes(kind));
    if (added) {
      // The kind in general — until one of its units is named.
      emit({ assets: value.assets, topics: [...value.topics.filter((tp) => tp !== added), added] });
    } else if (removed) {
      // Cleared whole: the topic and the units named under it.
      const nextAssets = value.assets.filter((id) => categoryOf(id) !== removed);
      emit({ assets: nextAssets, topics: value.topics.filter((tp) => tp !== removed && !impliedTopics(nextAssets, categoryOf).includes(tp)) });
    }
  };
  // The kinds whose units show: the answered ones, and the suggestion's.
  const openKinds = kinds.filter((kind) => answered(kind) || kind === suggestionCategory);
  const pickInKind = (kind: string, next: string[]) => {
    const others = value.assets.filter((id) => categoryOf(id) !== kind);
    const nextAssets = [...others, ...next];
    // Nothing named any more → the kind in general stays the answer.
    const nextTopics = [...value.topics.filter((tp) => tp !== kind), ...(next.length === 0 ? [kind] : [])];
    emit({ assets: nextAssets, topics: nextTopics.filter((tp) => !impliedTopics(nextAssets, categoryOf).includes(tp)) });
  };
  // The suggestion pinned first in its kind's list, under its certainty.
  const pinned = useMemo(() => {
    if (!suggestion) return null;
    const row = rowOf(suggestion.asset_id);
    if (!row) return null;
    return { row: optionRowOf(face(row), SUGGESTED), heading: suggestion.confidence !== null ? t('picker.suggestedSure', { value: formatNumber(Math.round(suggestion.confidence * 100)) }) : t('picker.suggested') };
  }, [suggestion, rowOf, face, t]);

  // ── the search: the catalog flattened — the matching units across kinds, the matching topics as chips ──
  const foundRows = useMemo(() => (typed ? search.rows.map((r) => optionRowOf(face(r), r.group)) : []), [typed, search.rows, face]);
  const foundTopics = typed ? topics.filter((tp) => matchesQuery(query, [tp.label, tp.value]) && !implied.includes(tp.value)) : [];
  // The units' answer settled on nothing (never a failed one — the list says "Couldn't load" then): the list stands down
  // behind the matching topics; with no topic matching either, "Nothing matches".
  const unitsEmpty = search.settled && !search.searching && !search.failed && foundRows.length === 0;
  const nothingFound = typed && unitsEmpty && foundTopics.length === 0;

  // On the search input: Escape cancels (an empty search — a full one clears first, the field's own); Enter picks the one thing the search left.
  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && onCancel) {
      // Cancel here, and no further — the peek's Escape stays out.
      e.preventDefault();
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === 'Enter' && typed && !search.searching) {
      const onlyUnit = search.rows.length === 1 && foundTopics.length === 0 ? search.rows[0] : null;
      const onlyTopic = foundTopics.length === 1 && search.rows.length === 0 ? foundTopics[0] : null;
      if (onlyUnit) {
        e.preventDefault();
        setAssets(value.assets.includes(onlyUnit.id) ? value.assets : [...value.assets, onlyUnit.id]);
        setQuery('');
      } else if (onlyTopic) {
        e.preventDefault();
        setTopics(value.topics.includes(onlyTopic.value) ? value.topics : [...value.topics, onlyTopic.value]);
        setQuery('');
      }
    }
  };

  // ── the tail topics, "+n more" as the row's last chip ──
  const tailShown = moreTopics ? tail : tail.filter((tp, i) => i < TOPIC_CAP || value.topics.includes(tp.value));
  const tailHidden = tail.length - tailShown.length;
  const tailTiles: ChoiceTileOption[] = [
    ...tailShown.map((tp) => ({ value: tp.value, label: tp.label, icon: iconOf(tp.value) })),
    ...(tailHidden > 0 || moreTopics ? [{ value: MORE, label: moreTopics ? t('picker.fewerTopics') : t('picker.moreTopics', { count: tailHidden }) }] : []),
  ];
  const pickTail = (next: string[]) => {
    if (next.includes(MORE)) {
      setMoreTopics((v) => !v);
      next = next.filter((v) => v !== MORE);
    }
    setTopics([...value.topics.filter((tp) => !tail.some((x) => x.value === tp)), ...next]);
  };

  const label = 'text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]';
  const rowSize = stacked ? 'touch' : 'default';

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <SearchField inputRef={searchRef} value={query} onChange={setQuery} onKeyDown={onSearchKeyDown} placeholder={t('picker.search')} ariaLabel={t('picker.search')} />
      {previous && !typed && (
        <Button variant="secondary" onClick={() => onChange(previous.value, namesOf(previous.value.assets))} className="justify-start">
          <icons.restore size={16} aria-hidden />
          <span className="truncate">{t('picker.samePrevious', { what: previous.label })}</span>
        </Button>
      )}
      {typed ? (
        // ── the search's flat answer ──
        nothingFound ? (
          <p className="text-sm text-[var(--color-text-secondary)]">{t('picker.noMatch')}</p>
        ) : (
          <>
            {!unitsEmpty && (
              <PickerList
                rows={foundRows}
                value={value.assets}
                onChange={setAssets}
                multiple
                text={query}
                loading={!search.settled && foundRows.length === 0 && !search.searching}
                searching={search.searching}
                failed={search.failed}
                hasMore={search.hasMore}
                settled={search.settled}
                ariaLabel={t('picker.assets')}
                rowSize={rowSize}
              />
            )}
            {foundTopics.length > 0 && (
              <ChoiceTiles multiple size="sm" ariaLabel={t('picker.topics')} options={foundTopics.map((tp) => ({ value: tp.value, label: tp.label, icon: iconOf(tp.value) }))} value={value.topics} onChange={setTopics} />
            )}
          </>
        )
      ) : (
        <>
          {/* ── the catalog: the kinds as chips — the answer; an answered one shows its units ── */}
          {kinds.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div className={label}>{t('picker.assets')}</div>
              <ChoiceTiles multiple size="sm" ariaLabel={t('picker.assets')} options={kindTiles} value={kinds.filter(answered)} onChange={toggleKind} />
              {openKinds.map((kind) => (
                <KindUnits
                  key={kind}
                  projectId={projectId}
                  memory={siteUnits}
                  kind={kind}
                  picked={namedIn(kind)}
                  onPick={(next) => pickInKind(kind, next)}
                  pinned={kind === suggestionCategory ? pinned : null}
                  ariaLabel={`${t('picker.assets')} · ${labelOf(kind)}`}
                  rowSize={rowSize}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)]">{t('picker.noAssets')}</p>
          )}
          {tail.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className={label}>{t('picker.topics')}</div>
              <ChoiceTiles multiple size="sm" ariaLabel={t('picker.topics')} options={tailTiles} value={value.topics.filter((tp) => tail.some((x) => x.value === tp))} onChange={pickTail} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** One kind's units — the unit lookup inside the site, one kind, at rest (the search box on top of the picker finds
 *  across kinds); 20 at a time, "More — keep typing" past them; the suggestion pinned first under its certainty. A
 *  window over the site's units: its rows are remembered on the site's own source (`memory`). */
function KindUnits({
  projectId,
  memory,
  kind,
  picked,
  onPick,
  pinned,
  ariaLabel,
  rowSize,
}: {
  projectId: string;
  /** The site's units — the source the kind narrows, and where its rows are remembered. */
  memory: LookupSource<'assets'>;
  kind: AssetCategory;
  picked: string[];
  onPick: (next: string[]) => void;
  pinned: { row: OptionRow; heading: string } | null;
  ariaLabel: string;
  rowSize: 'default' | 'touch';
}) {
  const face = useAssetFace();
  const source = useMemo<LookupSource<'assets'>>(() => ({ entity: 'assets', params: { ...memory.params, category: kind } }), [memory.params, kind]);
  const look = useLookup(projectId, source, [], { open: true, memory });
  const rows = useMemo(() => {
    const base = look.rows.map((r) => optionRowOf(face(r), null));
    return pinned ? [pinned.row, ...base.filter((r) => r.id !== pinned.row.id)] : base;
  }, [look.rows, face, pinned]);
  return (
    <PickerList
      rows={rows}
      value={picked}
      onChange={onPick}
      multiple
      groupLabel={(g) => (g === SUGGESTED && pinned ? pinned.heading : null)}
      loading={!look.settled && look.rows.length === 0}
      failed={look.failed}
      hasMore={look.hasMore}
      settled={look.settled}
      ariaLabel={ariaLabel}
      rowSize={rowSize}
    />
  );
}
